import { createAsyncThunk } from '@reduxjs/toolkit';
import { Hexabase } from 'hexabase-sdk';
import { Condition } from 'hexabase-sdk/dist/models';
import moment from 'moment';
import {
  CreateItem,
  CreateItemError,
  CreateItemRequest,
  CreateItemResponse,
  CreatePsgDetailsRequest,
  CreateSimpleRequest,
  MedicalSearchRequest,
  t_paymant_due_date,
  ThunkAPI,
  t_hospital_visit,
  t_cpap_instructions,
  ItemLinkInspecRequest,
  FilterHospitalResponse,
} from 'types';
import { billingProcessService, itemService, medicalService, registerService } from '../../services';
import { RegisterNewBillError } from '../../types/billingProcess';

export const getBillingItems = createAsyncThunk<any, { pageNumber: number; searchValue?: MedicalSearchRequest }>(
  'billingItems/Billing',
  async (params) => {
    try {
      const conditions: Condition[] = [];
      params.searchValue?.facility_name &&
        conditions.push({
          search_value: [params.searchValue.facility_name],
          data_type: 'text',
          id: 'facility_name',
          exact_match: false,
          include_null: false,
        });
      params.searchValue?.phone &&
        conditions.push({
          search_value: [params.searchValue.phone],
          data_type: 'text',
          id: 'facility_tel',
          exact_match: false,
          include_null: false,
        });
      params.searchValue?.code &&
        conditions.push({
          search_value: [params.searchValue.code],
          data_type: 'text',
          id: 'facility_code',
          exact_match: false,
          include_null: false,
        });

      const response = await medicalService.getMedicals({
        per_page: 20,
        page: params.pageNumber,
        conditions: conditions,
        use_display_id: true,
        sort_field_id: 'facility_code',
        sort_order: 'asc',
      });
      return response.data;
    } catch (e) {
      console.log(e);
    }
  },
);

export const getBillingItemsRegister = createAsyncThunk<
  any,
  { pageNumber: number; searchValue: { facility_id: string; invoice_month: string } }
>('billingItemsRegister/Billing', async (params) => {
  try {
    const BillingItemsRegister = await Hexabase.items().getItemsAsync({
      project_id: 'CPAP',
      datastore_id: 't_invoice',
      per_page: 20,
      page: params.pageNumber,
      use_display_id: true,
      sort_field_id: 'created_at',
      sort_order: 'desc',
      conditions: [
        {
          search_value: [params.searchValue.facility_id],
          data_type: 'string',
          id: 'facility_id',
          exact_match: true,
          include_null: false,
        },
        {
          search_value: [params.searchValue.invoice_month],
          data_type: 'text',
          id: 'invoice_month',
          exact_match: true,
          include_null: false,
        },
      ],
    });
    return BillingItemsRegister;
  } catch (e) {
    console.log(e);
  }
});

export const getBillingDetailsCPAPSpecial = createAsyncThunk<
  any,
  { facility_id: string; sp_price: string; invoice_month: string; page: 1; per_page: 0 }
>('billing/getItemDetailSpecial', async (params) => {
  try {
    const { data } = await billingProcessService.searchBillDetailCPAPSpecial(params);
    return data;
  } catch (e) {
    console.log(e);
  }
});

export const getBillingDetailsCPAP = createAsyncThunk<
  any,
  { facility_id: string; sp_price: string; invoice_month: string; page: 1; per_page: 0 }
>('billing/getItemDetails', async (params) => {
  try {
    const { data } = await billingProcessService.searchBillDetailCPAP(params);
    const cpapNames = data.items.map((v: any) => v.cpap_id);
    const cpap = await billingProcessService.getCPAPByPatientNames(cpapNames);
    const result = data.items.filter((v: any) => {
      return cpap.data.items.find((c: any) => c.patient_name === v.cpap_id);
    });
    data.items = result;
    data.totalItems = result.length;
    return data;
  } catch (e) {
    console.log(e);
  }
});

export const getBillingDetailsPSG = createAsyncThunk<
  any,
  { facility_id: string; invoice_month: string; page: 1; per_page: 0 }
>('billing/getItemDetailPSG', async (params) => {
  try {
    const { data } = await billingProcessService.searchBillDetailPSG(params);
    return data;
  } catch (e) {
    console.log(e);
  }
});

export const getBillingDetailsSimple = createAsyncThunk<
  any,
  { facility_id: string; invoice_month: string; page: 1; per_page: 0 }
>('billing/getItemDetailSimple', async (params) => {
  try {
    const { data } = await billingProcessService.searchBillDetailSimple(params);
    return data;
  } catch (e) {
    console.log(e);
  }
});

export const getBillingDetailNearest = createAsyncThunk<
  any,
  { facility_id: string; page: number; per_page: number; invoice_month: string }
>('billing/getItemDetailNearest', async (params) => {
  try {
    const { data } = await billingProcessService.searchBillDetailNearest(params);
    const curInvoice = data.items.findIndex((f) => f.invoice_month === params.invoice_month) ?? -1;
    if (curInvoice < (data.items.length ?? 0) - 1 && curInvoice > -1) {
      return data.items[curInvoice + 1];
    }
    return null;
  } catch (e) {
    console.log(e);
  }
});

export const getPaymentDueDateFromInvoiceMonth = createAsyncThunk<
  any,
  { invoice_month: string; page: number; per_page: number }
>('billing/getPaymentDueDate', async (params) => {
  try {
    const { data } = await billingProcessService.getPaymenDueDateFromInvoiceMonth(params);
    return data.items;
  } catch (e) {
    console.log(e);
  }
});

export const changeStatusBillInvoice = createAsyncThunk<any, { values: any; i_id: string }>(
  'billing/changeStatusBillInvoice',
  async (params) => {
    try {
      const { data } = await billingProcessService.changeStatusBillInvoice(params);
      return data;
    } catch (e) {
      console.log(e);
    }
  },
);

export const updateBillInvoice = createAsyncThunk<any, { values: any; i_id: string }>(
  'billing/updateBillInvoice',
  async (params) => {
    try {
      const { data } = await billingProcessService.updateBillInvoice(params);
      return data;
    } catch (e) {
      console.log(e);
    }
  },
);

export const updateLastBillInvoice = createAsyncThunk<any, { i_id: string }>(
  'billing/updateLastBillInvoice',
  async (params) => {
    try {
      const { data } = await billingProcessService.updateLastBillInvoice(params);
      return data;
    } catch (e) {
      console.log(e);
    }
  },
);

export const createItem = createAsyncThunk<CreateItemResponse, CreateItem, ThunkAPI<CreateItemError>>(
  'createPage/pages',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await billingProcessService.createItem(params);
      return data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error);
    }
  },
);

export const checkBill = createAsyncThunk<any, { facility_id: string; invoice_month: string | string[] }>(
  'billingProcess/checkBill',
  async (params) => {
    const { data } = await billingProcessService.getBillForFacility(params);
    if (data.items.length === 0) {
      return [];
    }

    const paymentCheck = data.items
      .filter((f: any) => f.Status !== 'payment')
      .map((v: any) => ({
        ...v,
        value: v.invoice_month,
        name: `${v.invoice_month.slice(0, 4)}年${v.invoice_month.slice(4)}月度`,
      }));

    if (paymentCheck.length === 1) {
      const nextMonth = moment()
        .year(Number(paymentCheck[0].invoice_month.slice(0, 4)))
        .month(Number(paymentCheck[0].invoice_month.slice(4)) - 1)
        .add(1, 'M');

      return [
        ...paymentCheck,
        {
          value: nextMonth.format('YYYYMM'),
          name: nextMonth.format('YYYY年MM月度'),
        },
      ];
    }
    return paymentCheck;
  },
);

export const getBillRegister = createAsyncThunk<any, { invoice_month: string; facility_id: string }>(
  'bill/getRegister',
  async (param) => {
    try {
      const { data } = await billingProcessService.searchBillRegister({
        facility_id: param.facility_id,
        invoice_month: param.invoice_month,
      });
      return data.items[0];
    } catch (error) {}
  },
);

export const registerNewBill = createAsyncThunk<
  any,
  { facility_id: string; invoice_month: string; facility_name: string },
  ThunkAPI<RegisterNewBillError>
>('register/newBill', async (params) => {
  try {
    const invoice = await billingProcessService.getBillForFacility(params);
    if (invoice.data.items.length > 0) {
      return invoice.data;
    }
    await billingProcessService.registerNewBill(params);
    const { data: newInvoice } = await billingProcessService.getBillForFacility(params);
    return newInvoice;
  } catch (error: any) {
    console.log(error);
  }
});

export const updateCreateItem = createAsyncThunk<CreateItemRequest, t_paymant_due_date>(
  'itemsCreate/updateItem',
  async (params) => {
    const { data } = await billingProcessService.updateItemCreate(params);
    return data;
  },
);

export const getCreateItem = createAsyncThunk<any, { item: string }>('itemsGet/getItem', async (params) => {
  const { data } = await billingProcessService.getProcessItemPayment(params);
  return data;
});

export const getAllCPAPExport = createAsyncThunk<any, string>('medical/GetDetail', async (params) => {
  try {
    const response = await billingProcessService.getAllCPAPByMedical(params);
    return response.data.items;
  } catch (error) {
    console.log(error);
  }
});

export const importInvoice = createAsyncThunk<any, { invoice_month: string; facility_id: string; facilityId: string }>(
  'getHospital/getItem',
  async (params) => {
    try {
      const { data } = await billingProcessService.filterHospitalVisitandLinks({
        ...params,
        page: 1,
        per_page: 0,
        include_item_ref: true,
      });
      const conditionsFilter: any[] = [
        {
          search_value: [params.facility_id],
          id: 'facility_name',
          exact_match: true,
          include_null: false,
        },
      ];
      const cpap = await itemService.filterCPAP({
        conditions: conditionsFilter,
        disable_include_links: true,
        disable_include_lookups: true,
      });
      const listCSV: any[] = [];
      const checkCandidateTargets: any[] = [];
      cpap.data.items.map((cpap) => {
        const dataFind = data.report_results.find((r) => r.cpap_id === cpap.cpap_id);
        if (dataFind) {
          if (Number(cpap.suspension_flg) === 1) {
            billingProcessService.deleteHospital({ i_id: dataFind.item_ref.csv_key.i_id });
            checkCandidateTargets.push(cpap.cpap_id);
          }
          return null;
        }
        if (Number(cpap.suspension_flg) === 1) {
          return null;
        }
        listCSV.push({
          target_month: params.invoice_month,
          invoice_month: '',
          facility_id: params.facilityId,
          cpap_id: cpap.cpap_id,
          target_month_num: +params.invoice_month,
          sp_price: cpap.sp_price,
          patient_id: cpap.patient_id,
          csv_key: `${params.invoice_month}_${cpap.i_id}`,
          hospital_visit: '',
          hospital_visit_remark: '',
          billing_type: '',
          _status_: '未請求',
          invoice_id: '',
          sort_num: cpap.sort_num || 99999,
          _delete_: '',
        });
        return {
          target_month: params.invoice_month,
          invoice_month: '',
          facility_id: params.facilityId,
          cpap_id: cpap.cpap_id,
          target_month_num: +params.invoice_month,
          sp_price: cpap.sp_price,
          patient_id: cpap.patient_id,
          csv_key: `${params.invoice_month}_${cpap.i_id}`,
          hospital_visit: '',
          hospital_visit_remark: '',
          billing_type: '',
          _status_: '未請求',
          invoice_id: '',
          sort_num: cpap.sort_num || 99999,
          _delete_: '',
        };
      });

      const changeStatusData = await billingProcessService.getBillingCandidate({
        conditions: [
          {
            id: 'cpap_id',
            search_value: checkCandidateTargets,
            exact_match: true,
          },
        ],
      });
      const changeStatusList: any[] = [];
      changeStatusData.data.report_results.forEach((value: any) => {
        changeStatusList.push(value.item_ref.hospital_visit.i_id);
      });
      billingProcessService.bulkAction({
        action: 'a_change_to_unpaid',
        conditions: [
          {
            id: 'i_id',
            search_value: changeStatusList,
          },
        ],
        item: {
          invoice_id: '',
          invoice_month: '',
          billing_type: 'o_unpaid',
        },
      });
      if (listCSV.length < 1) {
        return null;
      }
      const csvString = [
        [
          'target_month',
          'invoice_month',
          'facility_id',
          'cpap_id',
          'target_month_num',
          'sp_price',
          'patient_id',
          'csv_key',
          'hospital_visit',
          'hospital_visit_remark',
          'billing_type',
          '_status_',
          'invoice_id',
          'sort_num',
          '_delete_',
        ],
        ...listCSV.map((item) => Object.values(item)),
      ]
        .map((e) => e.join(','))
        .join('\n');
      const file = new File([csvString], 'test.csv', { type: 'text/csv;charset=utf-8;' });
      const importItems = await billingProcessService.importItems(file, listCSV.length);
      return importItems.data;
    } catch (error) {
      console.log(error);
    }
  },
);

export const getHospitalForPage = createAsyncThunk<
  any,
  {
    invoice_month: string;
    facility_id: string;
    page: number;
    per_page: number;
    conditions?:
      | Array<{
          conditions: Array<{
            search_value: Array<string>;
            id: string;
          }>;
          use_or_condition?: boolean;
        }>
      | Array<{
          search_value: Array<string>;
          id: string;
        }>;
  }
>('hospitalVisit/getHospitalForPage', async (params, { rejectWithValue }) => {
  try {
    const newHospitals = await billingProcessService.filterHospitalVisitandLinks({
      ...params,
      return_id_value_results: true,
      include_item_ref: true,
      sort_fields: [
        {
          id: 'sort_num',
          order: 'asc',
        },
        {
          id: 'created_at',
          order: 'asc',
        },
      ],
    });
    const fields = await registerService.getDataStoreFields({ appId: 'CPAP', dataStoreId: 't_hospital_visit' });
    return {
      hospitals: newHospitals?.data
        ? {
            items: newHospitals.data?.report_results,
            totalItems: newHospitals.data?.totalItems,
          }
        : null,
      fieldHospital: fields.data ? Object.entries(fields.data.fields) : [],
    };
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getFieldHospital = createAsyncThunk<any>('billingProcess/getFieldHospital', async () => {
  try {
    const fields = await registerService.getDataStoreFields({ appId: 'CPAP', dataStoreId: 't_hospital_visit' });
    return fields.data ? Object.entries(fields.data.fields) : [];
  } catch (error) {}
});

export const getHospitalExport = createAsyncThunk<
  FilterHospitalResponse,
  { invoice_month: string; facility_id: string; facilityId: string }
>('hospitalVisit/getHospitalExport', async (params, { rejectWithValue }) => {
  try {
    const { data } = await billingProcessService.filterHospitalVisitandLinks({
      ...params,
      per_page: 0,
      page: 1,
      return_id_value_results: true,
      sort_fields: [
        {
          id: 'sort_num',
          order: 'asc',
        },
        {
          id: 'created_at',
          order: 'asc',
        },
      ],
    });

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateHospital = createAsyncThunk<any, { invoice_month: string; hospital: t_hospital_visit }>(
  'hospitalVisit/updateHospital',
  async (params) => {
    let invoice_month = null;

    if (params.hospital.billing_type === 'o_normal' || params.hospital.billing_type === 'o_normal_past_no_claim') {
      invoice_month = params.invoice_month;
    }

    const resultUpdate = await billingProcessService.updateHospital({
      i_id: params.hospital.i_id,
      hospital_visit: params.hospital.hospital_visit,
      hospital_visit_remark: params.hospital.hospital_visit_remark,
      billing_type: params.hospital.billing_type,
      invoice_month: params.hospital.billing_type && invoice_month,
    });
    return resultUpdate;
  },
);

export const updatePsgDetailsCreate = createAsyncThunk<any, CreatePsgDetailsRequest>(
  'itemsCreate/updateItem',
  async (params) => {
    const detailsItem = params?.datePSG?.map((itemValue) => {
      return {
        operation: 1,
        link_to_parent: true,
        action_id: '',
        item: {
          name: itemValue?.name,
          invoice_month: params.item?.invoice_month,
          facility_id: params.item?.facility_id,
          inspection_day: itemValue.inspection_day,
          psg_remark: itemValue.psg_remark,
        },
      };
    });
    const newParam = { ...params, related_ds_items: detailsItem };
    const { data } = await billingProcessService.updatePsgDetailsCreate(newParam);
    return data;
  },
);
export const importCSV = createAsyncThunk<
  any,
  { filename: string; file: string; optionField: any[]; invoice_month: string }
>('importCSV/billing', async (params, { rejectWithValue }) => {
  try {
    const newCSVArr = params.file.split('\n');
    const newCSVFormat = newCSVArr.map((data, index) => {
      if (index === 0) {
        return 'hospital_visit,hospital_visit_remark,billing_type,csv_key,invoice_month';
      }
      const newData = data.split(',').slice(2);
      const hospital_visit = params.optionField.find((f) => f.value === newData[0]);

      if (
        hospital_visit &&
        hospital_visit?.display_id === 'o_yes' &&
        (newData[2] === '受診' || newData[2] === '受診（未受診分請求なし）')
      ) {
        newData.push(params.invoice_month);
      } else {
        newData.push('');
      }
      if (newData[3]) {
        if (JSON.stringify(newData[3]).includes('\\r\\n')) {
          newData[3] = JSON.parse(JSON.stringify(newData[3]).replace('\\r\\n', ''));
        } else if (JSON.stringify(newData[3]).includes('\\r')) {
          newData[3] = JSON.parse(JSON.stringify(newData[3]).replace('\\r', ''));
        } else {
          newData[3] = JSON.parse(JSON.stringify(newData[3]).replace('\\n', ''));
        }
      }

      return newData.join(',');
    });
    const formatString = newCSVFormat.join('\n');

    const file = new File([formatString], 'test.csv', { type: 'text/csv;charset=utf-8;' });
    const { data } = await billingProcessService.importCSV({
      ...params,
      file: file,
      importCount: newCSVArr.length - 2,
      appendFalse: true,
    });
    return data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const updateSimple = createAsyncThunk<any, CreateSimpleRequest>('register/Equipment', async (params) => {
  const detailsItem = params?.data_related.map((itemValue) => {
    return {
      operation: 1,
      link_to_parent: true,
      action_id: '',
      item: {
        name: itemValue?.name,
        invoice_month: params.invoice_month,
        facility_id: params.facility_id,
        inspection_day: itemValue.inspection_day,
        simple_inspection_remark: itemValue.simple_inspection_remark,
      },
    };
  });
  const response = await billingProcessService.updateSimple({ ...params, data_related: detailsItem });
  return response;
});

export const getHospitalByCPAP = createAsyncThunk<any, { cpap_id: string; page: number }>(
  'hospitalVisit/getHospitalByCPAP',
  async (param) => {
    try {
      const result = await billingProcessService.getHospitalForCPAP(param);
      return result.data;
    } catch (error) {
      console.log(error);
    }
  },
);

export const getHospitalStep2 = createAsyncThunk<
  any,
  { facility_id: string; invoice_month: string; facility_name: string; invoice_id: string; statusBill: string }
>('hospitalVisit/getHospitalStep2', async (params) => {
  try {
    const firstHospitals = await billingProcessService.searchReportHospital({
      per_page: 0,
      page: 1,
      conditions: [
        {
          id: 'facility_id',
          search_value: [params.facility_id],
        },
        {
          id: 'target_month',
          search_value: [params.invoice_month],
        },
        {
          id: 'hospital_visit',
          search_value: ['o_yes'],
        },
        {
          id: 'billing_type',
          search_value: ['o_normal', 'o_no_claim'],
        },
      ],
    });
    const candidateData = await billingProcessService.getBillingCandidate({
      conditions: [
        {
          id: 'facility_name',
          search_value: [params.facility_id],
        },
        {
          id: 'billing_type',
          search_value: ['o_unpaid_collection'],
          not_match: true,
        },
      ],
    });
    const targetBillUpdate: string[] = [];
    const targetBillDelete: string[] = [];
    candidateData.data.report_results.forEach((value: any) => {
      const find = firstHospitals.data.report_results.find((v: any) => {
        if (v.cpap_id === value.cpap_id && v.facility_id === value.facility_id) {
          return true;
        }
      });
      if (find) {
        targetBillUpdate.push(value.item_ref.hospital_visit.i_id);
      } else {
        targetBillDelete.push(value.item_ref.hospital_visit.i_id);
      }
    });
    if (params.statusBill !== '確定') {
      await Promise.all([
        billingProcessService.bulkAction({
          action: 'a_change_to_billable',
          conditions: [
            {
              id: 'i_id',
              search_value: targetBillUpdate,
            },
            {
              id: 'invoice_month',
              search_value: ['2'],
              not_match: true,
            },
          ],
          item: {
            invoice_month: params.invoice_month,
            billing_type: 'o_unpaid_collection',
          },
          comment: '過去の未受診明細自動処理：未受診分を請求対象へ',
          max_items: 300,
        }),
        billingProcessService.bulkAction({
          action: 'a_change_to_unpaid',
          conditions: [
            {
              id: 'i_id',
              search_value: targetBillDelete,
            },
            {
              id: 'invoice_month',
              search_value: ['2'],
            },
            {
              id: 'billing_type',
              search_value: ['o_recovery'],
              not_match: true,
            },
          ],
          item: {
            invoice_id: '',
            invoice_month: '',
            billing_type: 'o_unpaid',
          },
          comment: '過去の未受診明細自動処理：請求対象外へ',
          max_items: 300,
        }),
        billingProcessService.bulkAction({
          action: 'a_change_to_unpaid',
          conditions: [
            {
              id: 'target_month',
              search_value: [params.invoice_month],
            },
            {
              id: 'billing_type',
              search_value: ['o_unpaid', 'o_no_claim', 'o_no_claim_for_period'],
              include_null: true,
            },
            {
              id: 'invoice_id',
              search_value: [params.invoice_id],
            },
          ],
          item: {
            invoice_id: '',
          },
          comment: '請求対象外に戻す',
          max_items: 300,
        }),
      ]);
    }

    const lastResult = await billingProcessService.searchHospital({
      per_page: 50,
      page: 1,
      conditions: [
        {
          id: 'facility_id',
          search_value: [params.facility_id],
        },
        {
          id: 'invoice_month',
          search_value: [params.invoice_month],
        },
        {
          id: 'billing_type',
          search_value: ['o_normal', 'o_unpaid_collection', 'o_normal_past_no_claim'],
        },
      ],
      sort_fields: [
        {
          id: 'sort_num',
          order: 'asc',
        },
        {
          id: 'cpap_id',
          order: 'asc',
        },
        {
          id: 'target_month_num',
          order: 'desc',
        },
      ],
      include_lookups: false,
    });
    const cpapNames = lastResult.data.items.map((v: any) => v.cpap_id);
    const cpap = await billingProcessService.getCPAPByPatientNames(cpapNames);
    const cpapItems = lastResult.data.items.filter((v: any) => {
      return cpap.data.items.find((c: any) => c.patient_name === v.cpap_id);
    });
    lastResult.data.items = cpapItems;
    return lastResult.data;
  } catch (error) {}
});

export const getPastHospital = createAsyncThunk<any, { facility_id: string; invoice_month: string }>(
  'hospital/getPastHospital',
  async (params) => {
    try {
      const conditions: any[] = [
        {
          id: 'facility_id',
          search_value: [params.facility_id],
        },
        {
          id: 'invoice_month',
          search_value: [params.invoice_month],
        },
        {
          id: 'billing_type',
          search_value: ['o_recovery', 'o_advance_payment'],
        },
      ];
      const { data } = await billingProcessService.searchHospital({
        per_page: 0,
        page: 1,
        conditions: conditions,
        sort_fields: [
          { id: 'sort_num', order: 'asc' },
          { id: 'cpap_id', order: 'asc' },
          { id: 'target_month_num', order: 'asc' },
        ],
        include_lookups: false,
      });
      return data.items;
    } catch (error) {
      console.log(error);
    }
  },
);

export const getCPAPByFacility = createAsyncThunk<any, { facility_id: string; searchValue?: string }>(
  'hospital/getCPAPByFacility',
  async (param) => {
    try {
      const conditions: any[] = [
        {
          search_value: [param.facility_id],
          id: 'facility_name',
          exact_match: true,
          include_null: false,
        },
      ];
      param.searchValue &&
        conditions.push({
          conditions: [
            { id: 'patient_id', search_value: [param.searchValue] },
            { id: 'patient_name', search_value: [param.searchValue] },
          ],
          use_or_condition: true,
        });
      const { data } = await itemService.filterCPAP({ conditions: conditions, per_page: 20 });
      return data.items;
    } catch (error) {
      console.log(error);
    }
  },
);

export const pageHospitalStep2 = createAsyncThunk<any, any>('hospital/pageHospitalStep2', async (params) => {
  try {
    const lastResult = await billingProcessService.searchHospital({
      per_page: 50,
      page: params.page,
      conditions: [
        {
          id: 'facility_id',
          search_value: [params.facility_id],
        },
        {
          id: 'invoice_month',
          search_value: [params.invoice_month],
        },
        {
          id: 'billing_type',
          search_value: ['o_normal', 'o_unpaid_collection', 'o_normal_past_no_claim'],
        },
      ],
      sort_fields: [
        { id: 'sort_num', order: 'asc' },
        { id: 'cpap_id', order: 'asc' },
        { id: 'target_month_num', order: 'asc' },
      ],
      include_lookups: false,
    });
    return lastResult.data;
  } catch (error) {}
});

export const checkHospitalAdd = createAsyncThunk<any, any>('hospital/checkHospitalAdd', async (param) => {
  try {
    const checkExistData = await billingProcessService.searchHospital({
      per_page: 20,
      page: 1,
      conditions: [
        {
          id: 'csv_key',
          search_value: [`${param.year}${param.month}_${param.cpap_id}`],
        },
      ],
    });
    if (checkExistData.data.totalItems < 1) {
      return 0;
    }
    if (Number(param.year + param.month) > Number(param.invoiceMonth)) {
      return checkExistData.data.items[0];
    }

    const { data } = await billingProcessService.searchHospital({
      per_page: 20,
      page: 1,
      conditions: [
        {
          id: 'status',
          search_value: ['unbilled'],
        },
        {
          id: 'hospital_visit',
          search_value: ['o_no'],
          include_null: true,
        },
        {
          id: 'billing_type',
          search_value: ['o_unpaid'],
          include_null: true,
        },
        {
          id: 'csv_key',
          search_value: [`${param.year}${param.month}_${param.cpap_id}`],
        },
      ],
      sort_fields: [{ id: 'target_month', order: 'asc' }],
    });
    if (data.totalItems < 1) {
      return 1;
    }
    return data.items[0];
  } catch (error) {
    console.log(error);
  }
});

export const DetailCheck = createAsyncThunk<any, any>('hospital/DetailCheck', async (param) => {
  try {
    if (param.i_id) {
      billingProcessService.updateHospital(param);
    } else {
      const { data } = await billingProcessService.createHospital(param);
      return data;
    }
  } catch (error) {}
});

export const deletePastHospital = createAsyncThunk<any, any>('hospital/deletePastHospital', async (param) => {
  if (param.billing_type === '先払い') {
    return billingProcessService.deleteHospital({ i_id: param.i_id });
  }
  if (param.billing_type === '過去未収分') {
    const dataUpdate = {
      i_id: param.i_id,
      invoice_id: '',
      invoice_month: '',
      billing_type: 'o_unpaid',
      Status: 'unbilled',
    };
    return billingProcessService.updateHospital(dataUpdate);
  }
});

export const getCPAPExcel = createAsyncThunk<any, any>('billingprocess/getCPAPExcel', async (param) => {
  try {
    const res = await itemService.getItemDetail({ d_id: 't_medical_institution', i_id: param });
    const cpap = res.data.linked_items.t_cpap_instructions.items.filter(
      (f: t_cpap_instructions) => f.suspension_flg?.toString() !== '1',
    );
    return cpap;
  } catch (error) {
    return [];
  }
});

export const getPatientsCPAP = createAsyncThunk<any, any>('billingprocess/getPatientsCPAP', async (param) => {
  try {
    const res = await itemService.filterCPAP({
      conditions: [
        {
          id: 'facility_name',
          search_value: [param],
        },
        {
          id: 'suspension_flg',
          search_value: ['1'],
          not_match: true,
        },
      ],
      disable_include_links: true,
      disable_include_lookups: true,
    });

    return res.data.items.map((t: t_cpap_instructions) =>
      'sort_num' in t && t.sort_num !== null ? { ...t, sort_num: Number(t.sort_num) } : t,
    );
  } catch (error) {
    return [];
  }
});

export const getResultImport = createAsyncThunk<any, any>('billingprocess/getResultImport', async (param) => {
  try {
    const response = await billingProcessService.getImportResult(param);
    return response.data;
  } catch (error) {}
});

export const getItemLinkPSG = createAsyncThunk<any, any>('billingprocess/getItemLinkPSG', async (param) => {
  try {
    const response = await billingProcessService.getInvoiceDetailPSG(param);
    return response.data.report_results;
  } catch (error) {}
});

export const getItemLinkInspec = createAsyncThunk<any, ItemLinkInspecRequest>(
  'billingprocess/getItemLinkINSEPC',
  async (param) => {
    try {
      const { data } = await billingProcessService.getItemLinkInspec(param);

      return data.report_results;
    } catch (error) {}
  },
);

export const updateHospitalByCSV = createAsyncThunk<any, any>('billingProcess/updateHospitalByCSV', async (param) => {
  try {
    const listCSV: any[] = [];

    if (param.dataHospital.length < 1) {
      return null;
    }
    param.dataHospital.map((value: t_hospital_visit) => {
      listCSV.push({
        csv_key: value.csv_key,
        _status_: '請求済み',
        invoice_id: param.invoice_id,
      });
      return value;
    });

    const csvString = [['csv_key', '_status_', 'invoice_id'], ...listCSV.map((item) => Object.values(item))]
      .map((e) => e.join(','))
      .join('\n');
    const file = new File([csvString], 'test.csv', { type: 'text/csv;charset=utf-8;' });

    const importItems = await billingProcessService.importItems(file, listCSV.length, true);
    return importItems.data;
  } catch (error) {
    return null;
  }
});

export const getFieldEditInvoice = createAsyncThunk<any>('billingProcess/getFieldInvoice', async () => {
  try {
    const { data } = await registerService.getDataStoreFields({ appId: 'CPAP', dataStoreId: 't_invoice' });
    const fields: any = Object.entries(data.fields)?.find(
      ([_key, value]: [string, any]) => value.display_id === 'editing_status_by_clinic',
    );
    return fields ? fields[1].options : [];
  } catch (error) {
    return [];
  }
});

export const deletePSG = createAsyncThunk<any, any>('billingProcess/deletePSG', async (param) => {
  try {
    return billingProcessService.deletePSG({ i_id: param });
  } catch (error) {
    return null;
  }
});

export const deleteInSpection = createAsyncThunk<any, any>('billingProcess/deleteInSpection', async (param) => {
  try {
    return billingProcessService.deleteInSpection({ i_id: param });
  } catch (error) {
    return null;
  }
});

export const getListOtherDetail = createAsyncThunk<any, any>('billProcess/getListOtherDetail', async (param) => {
  try {
    const { data } = await billingProcessService.getInvoiceOtherDetail({ invoice_id: param?.invoice_id });
    return data.report_results;
  } catch (error) {
    return [];
  }
});

export const updateOtherDetail = createAsyncThunk<any, any>('billingProcess/updateOtherDetail', async (params) => {
  try {
    const detailsItem = params?.newData.map((itemValue: any) => {
      return {
        operation: 1,
        link_to_parent: true,
        action_id: '',
        item: {
          detail_name: itemValue?.detail_name,
          invoice_month: params.invoice_month,
          facility_id: params.facility_id,
          other_detail_count: itemValue.other_detail_count,
          other_detail_unit_price: itemValue.other_detail_unit_price,
          other_detail_remark: itemValue.other_detail_remark,
        },
      };
    });
    const { data } = await billingProcessService.updateOtherDetail({
      invoice_id: params.invoice_id,
      related_ds_items: detailsItem,
    });
    return data;
  } catch (error) {
    return error;
  }
});

export const deleteOther = createAsyncThunk<any, any>('billing/deleteOther', async (param) => {
  try {
    const related_ds_items = [
      {
        operation: 3,
        action_id: '',
        i_id: param.other_id,
      },
    ];
    const { data } = await billingProcessService.updateOtherDetail({
      invoice_id: param.invoice_id,
      related_ds_items: related_ds_items,
    });
    return data;
  } catch (error) {
    return error;
  }
});

export const removeHospital = createAsyncThunk<any, { i_id: string }>(
  'hospitalVisit/removeHospital',
  async (params) => {
    const resultUpdate = await billingProcessService.updateHospital({
      i_id: params.i_id,
      billing_type: null,
      hospital_visit: null,
      invoice_month: null,
    });

    return resultUpdate;
  },
);

export const makeTrueConsultationStatus = createAsyncThunk<
  { has_error: boolean; result: any },
  { facility_id: string; invoice_month: string },
  ThunkAPI<any>
>('hospitalVisit/makeTrueConsultationStatus', async (params, { rejectWithValue }) => {
  try {
    const { data } = await billingProcessService.makeTrueConsultationStatus(params);

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});
